import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { COLLECT_LOAN_APPLICATION_STORE_KEY } from "@krea/customer-features/store/queryClient";
import {
  BANKID_FLOW_STATES,
  HINT_CODE_STATES,
} from "@krea/customer-features/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { RemoteIdentificationType } from "../../../utils";

import {
  useCancelBankIDForApplication,
  useCollectQuery,
  useInitiateMutation,
} from "./utils";

export const useInitiateLoanApplication = () => {
  const queryClient = useQueryClient();
  const remoteIdentificationType = useLoanApplicationFormStore(
    (state) => state.remoteIdentification.type,
  );
  const [isInitiated, setIsInitiated] = useState(false);

  const { data: collectResponse = {} } = useCollectQuery();
  const {
    mutate: initiateLoanApplication,
    data: { initiatePendingLoanApplication: mutationData } = {},
    error: mutationError,
    reset: resetMutationState,
  } = useInitiateMutation(() => setIsInitiated(true));

  const { mutate: cancelApplicationViaBankID } =
    useCancelBankIDForApplication();

  const status =
    collectResponse.status || mutationError?.status || mutationData?.status;
  const hintCode =
    collectResponse.hintCode ||
    mutationError?.hintCode ||
    mutationData?.hintCode;

  const errorCode = collectResponse.errorCode || mutationError?.errorCode;

  const isPending = status === BANKID_FLOW_STATES.PENDING;
  const isFailed = status === BANKID_FLOW_STATES.FAILED;
  const isComplete = status === BANKID_FLOW_STATES.COMPLETE;

  const isError = isFailed && hintCode === HINT_CODE_STATES.ERROR;
  const isUserCancel = isFailed && hintCode === HINT_CODE_STATES.USER_CANCEL;

  const resetQuery = async () => {
    await queryClient.resetQueries([COLLECT_LOAN_APPLICATION_STORE_KEY]);
  };

  const cancel = (props) => {
    if (
      isPending &&
      remoteIdentificationType === RemoteIdentificationType.BANK_ID
    ) {
      cancelApplicationViaBankID();
    } else {
      queryClient.setQueryData([COLLECT_LOAN_APPLICATION_STORE_KEY], {
        status: null,
      });
    }
    resetMutationState();
    setIsInitiated(false);
  };

  return {
    isPending,
    isFailed,
    isComplete,
    isInitiated,
    isError,
    isUserCancel,
    initiateLoanApplication,
    cancelApplicationViaBankID: cancel,
    resetQuery,
    errorCode,
  };
};
